import { createSlice, createAsyncThunk, Action } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { type UserSnapshot } from 'talkjs/all';
import {
  fetchAlertAdrICSP,
  fetchInitialAlertAdr,
} from '@rsos/components/capstone/DataCards/adrDataSlice';
import {
  ALERTDATAEVENT,
  parseDataByCategories,
  parseNestedData,
} from '@rsos/components/capstone/DataCards/utils';
import {
  ACCEPTED,
  DECLINED,
  DISPATCH_REQUESTED,
} from '@rsos/constants/alertStatuses';
import { CONVERSATION_ENDED } from '@rsos/constants/chatStatuses';
import { PRODUCT_TOKEN } from '@rsos/sinatra/src/actions/types';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { checkStatus, parseJSON } from '@rsos/utils';
import { getAPIHost } from '@rsos/utils/metaTags';
import serializeRequestError from '@rsos/utils/serializeRequestError';
import { ALERT_PREFIX, CHAT_RAPTOR_PREFIX } from '@rsos/utils/talkJS';
import {
  deleteAlertConversation,
  getAlertConversationStatus,
} from './alertsAPI';
import {
  type ChatHistoryPayload,
  type ChatState,
  type ConversationArg,
  type PrivilegedConversation,
} from './chatSlice.types';
import { receivedAlertUpdatedStatus } from './incidentsSlice';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_UPDATE = 'STATUS_UPDATE';

const LOGOUT_ACTIONS = new Set([
  'FORCED_LOGOUT_SUCCESS',
  'LOGOUT_SUCCESS',
  'SINATRA_LOGOUT',
]);

const isLogoutAction = (action: Action): boolean =>
  LOGOUT_ACTIONS.has(action.type);

/**
 * Parse the `Suggested Script Responses` parent object out of the adr and use it only
 * when it's available. There are 2 possible structures:
 * 1. { "Chatbot Responses": { ... }, "Suggested Script Responses": { ... } }
 * 2. { "Suggested Script Responses": { ... } }
 */
export const parseScriptedResponses = (
  adr: Record<string, unknown>,
): Record<string, unknown> => {
  const scriptedResponsesKey = 'Suggested Script Responses';
  let scriptedResponses: Record<string, unknown> = {};

  const parseScriptedResponsesJsonPath = (data: unknown) => {
    if (typeof data !== 'object' || data === null) return;
    const keys = Object.keys(data as object);
    keys.forEach(key => {
      if (key === scriptedResponsesKey) {
        scriptedResponses = data as Record<string, unknown>;
      } else {
        parseScriptedResponsesJsonPath((data as Record<string, unknown>)[key]);
      }
    });
  };

  parseScriptedResponsesJsonPath(adr);
  return scriptedResponses;
};

const reduceAlertDataEvent = (
  adr: Record<string, unknown>,
): Record<string, unknown> => {
  const topLevelKeys: string[] = [];
  for (const key of Object.keys(adr)) {
    const entry = adr[key] as { meta?: Record<string, { category: string }> };
    for (const subKey of Object.keys(entry?.meta ?? {})) {
      if (entry?.meta?.[subKey]?.category === 'alertdataevent') {
        topLevelKeys.push(key);
      }
    }
  }

  return Object.keys(adr)
    .filter(key => !topLevelKeys.includes(key))
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = adr[key];
      return acc;
    }, {});
};

// ─── Thunks ──────────────────────────────────────────────────────────────────

/**
 * Fetch an alert's chat history to render all messages prior to alert creation.
 * Data is sorted by Platform (ascending) → Epoch (ascending) by default.
 * Pass ascending=0 for Epoch descending; ascending=1 (or any other value) for ascending.
 */
export const fetchAlertConversationHistory = createAsyncThunk<
  ChatHistoryPayload,
  string
>(
  'chat/fetchAlertConversationHistory',
  async (alertId, { rejectWithValue }) => {
    const token = localStorage.getItem(PRODUCT_TOKEN);
    const midasAlertId = alertId.split('alert-')[1];
    try {
      const data = await fetch(
        `${getAPIHost()}/v1/retriever/chat?alert_id=${midasAlertId}&ascending=1`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        },
      )
        .then(checkStatus)
        .then(parseJSON)
        .catch((error: unknown) => Promise.reject(error));
      return data as unknown as ChatHistoryPayload;
    } catch (error) {
      const serializedError = serializeRequestError(error as object);
      return rejectWithValue(serializedError);
    }
  },
);

export const deleteConversation = createAsyncThunk<unknown, ConversationArg>(
  'chat/deleteConversation',
  async ({ orgName, conversationID }, { rejectWithValue }) => {
    try {
      const response = await deleteAlertConversation(
        orgName ?? '',
        conversationID,
      );
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const fetchAlertConversationStatus = createAsyncThunk<
  { data: { status?: string } },
  ConversationArg
>(
  'chat/fetchAlertConversationStatus',
  async ({ orgName, conversationID }, { rejectWithValue }) => {
    try {
      const response = await getAlertConversationStatus(
        orgName ?? '',
        conversationID,
      );
      return { data: response.data };
    } catch (error: unknown) {
      const errorObject = {
        statusCode: (error as { response?: { status?: number } })?.response
          ?.status,
        data: (error as { response?: { data?: unknown } })?.response?.data,
      };
      return rejectWithValue(errorObject);
    }
  },
);

// ─── Slice ───────────────────────────────────────────────────────────────────

export const initialState: ChatState = {
  chats: {},
  loading: {
    alertAdr: false,
    alertConversationHistory: false,
    alertConversationStatus: false,
    alertEndChat: false,
  },
  error: {
    alertAdr: null,
    alertConversationHistory: null,
    alertConversationStatus: null,
    alertEndChat: null,
  },
  selectedAlertID: null,
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChatSelectedAlertID: (state, action) => {
      state.selectedAlertID = action.payload;
    },
    addPrivilegedParticipants: (state, action: { payload: UserSnapshot[] }) => {
      const entry = state.chats[state.selectedAlertID ?? ''];
      if (entry?.privileged_conversation) {
        entry.privileged_conversation.participants = action.payload;
      }
    },
    addUnreadMessages: (
      state,
      action: { payload: { alertID: string; messageID: string } },
    ) => {
      const { alertID, messageID } = action.payload;
      const privileged_conversation =
        state.chats[alertID]?.privileged_conversation;

      // TalkJS unreadMessageCount is unreliable — it can switch to 1 on its own.
      // Tracking by message ID array is more reliable for unread counts.
      if (!privileged_conversation) return;

      if (!privileged_conversation.unreadMessages) {
        privileged_conversation.unreadMessages = [messageID];
      } else if (!privileged_conversation.unreadMessages.includes(messageID)) {
        privileged_conversation.unreadMessages = [
          ...privileged_conversation.unreadMessages,
          messageID,
        ];
      }
    },
    resetUnreadMessages: (state, action: { payload: string }) => {
      const entry = state.chats[action.payload];
      if (entry?.privileged_conversation) {
        entry.privileged_conversation.unreadMessages = [];
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase('incidents/viewAllAlerts', state => {
        state.selectedAlertID = null;
      })
      .addCase(receivedAlertUpdatedStatus, (state, action) => {
        const alertId = action.payload.alert_id as string;
        if (
          action.payload.message_type === STATUS_UPDATE &&
          (action.payload.message.name === ACCEPTED ||
            action.payload.message.name === DECLINED)
        ) {
          state.chats[alertId] = {
            ...state.chats[alertId],
            alertResponseBy: action.payload.sender,
          };
        }
        if (
          action.payload.message_type === STATUS_UPDATE &&
          action.payload.message.name === DISPATCH_REQUESTED
        ) {
          state.chats[alertId] = {
            ...state.chats[alertId],
            alertRquestedBy: action.payload.sender,
          };
        }

        if (state.chats[alertId]) {
          if (!state.chats[alertId].logs) {
            state.chats[alertId] = {
              ...state.chats[alertId],
              logs: [],
            };
          }
          (state.chats[alertId].logs as unknown[]).push(action.payload);
        }
      })
      .addCase(fetchAlertConversationHistory.pending, state => {
        state.loading.alertConversationHistory = true;
        state.error.alertConversationHistory = null;
      })
      .addCase(fetchAlertConversationHistory.rejected, (state, action) => {
        state.loading.alertConversationHistory = false;
        state.error.alertConversationHistory = action.payload;
      })
      .addCase(fetchAlertConversationHistory.fulfilled, (state, action) => {
        const alertID = `alert-${action.payload.alert_id}`;
        return {
          ...state,
          chats: {
            ...state.chats,
            [alertID]: {
              ...state.chats[alertID],
              ...action.payload.chat_messages,
            },
          },
          loading: { ...state.loading, alertConversationHistory: false },
          error: { ...state.error, alertConversationHistory: null },
        };
      })
      .addCase(fetchInitialAlertAdr.pending, state => {
        state.loading.alertAdr = true;
        state.error.alertAdr = null;
      })
      .addCase(fetchInitialAlertAdr.rejected, (state, action) => {
        state.loading.alertAdr = false;
        state.error.alertAdr = action.payload;
      })
      .addCase(fetchInitialAlertAdr.fulfilled, (state, action) => {
        const alertID = action.meta.arg as unknown as string;
        const alertDataEventKeys = ['privileged_conversation'];

        const parsedDataByCategories = parseDataByCategories(action.payload, [
          ALERTDATAEVENT,
        ]);

        const parsedData = alertDataEventKeys
          .map(key => parseNestedData(parsedDataByCategories ?? {}, key))
          .reduce<Record<string, unknown>>((acc, data) => {
            const record = data as Record<string, unknown>;
            const keyName = Object.keys(record)[0];
            acc[keyName] = record[keyName];
            return acc;
          }, {});

        const parsedPrivConv = parsedData.privileged_conversation as
          | Partial<PrivilegedConversation>
          | undefined;

        return {
          ...state,
          chats: {
            ...state.chats,
            [alertID]: {
              ...state.chats[alertID],
              ...(parsedPrivConv && {
                privileged_conversation: {
                  ...state.chats[alertID]?.privileged_conversation,
                  ...parsedPrivConv,
                },
              }),
            },
          },
        };
      })
      .addCase(fetchAlertAdrICSP.pending, state => {
        state.loading.alertAdr = true;
        state.error.alertAdr = null;
      })
      .addCase(fetchAlertAdrICSP.rejected, (state, action) => {
        state.loading.alertAdr = false;
        state.error.alertConversationHistory = action.payload;
      })
      .addCase(fetchAlertAdrICSP.fulfilled, (state, action) => {
        const alertID = action.meta.arg as unknown as string;
        const reducedAlertDataEvent = reduceAlertDataEvent(
          action.payload as Record<string, unknown>,
        );
        const parsedScriptedResponses = parseScriptedResponses(
          reducedAlertDataEvent,
        );

        if (Object.keys(parsedScriptedResponses).length) {
          if (state.chats?.[alertID]?.adr) {
            // Deep clone the chat entry before merging the new ADR data.
            const currentChat = JSON.parse(
              JSON.stringify(state.chats[alertID]),
            );
            return {
              ...state,
              chats: {
                ...state.chats,
                [alertID]: {
                  ...state.chats[alertID],
                  adr: { ...currentChat.adr, ...parsedScriptedResponses },
                },
              },
            };
          } else {
            return {
              ...state,
              chats: {
                ...state.chats,
                [alertID]: { adr: parsedScriptedResponses },
              },
            };
          }
        } else {
          if (state.chats[alertID]) {
            return {
              ...state,
              chats: {
                ...state.chats,
                [alertID]: { ...state.chats[alertID], adr: {} },
              },
            };
          } else {
            return {
              ...state,
              chats: { ...state.chats, [alertID]: { adr: {} } },
            };
          }
        }
      })
      .addCase(fetchAlertConversationStatus.pending, state => {
        state.loading.alertConversationStatus = true;
      })
      .addCase(fetchAlertConversationStatus.rejected, (state, action) => {
        state.loading.alertConversationStatus = false;
        const conversationID = (action?.meta?.arg as ConversationArg)
          ?.conversationID;
        if (!conversationID) return;

        if (conversationID.includes('default-')) {
          const alertID = `alert-${conversationID.split('default-')[1]}`;
          if (state.chats[alertID]) {
            // If the API request returns a 404, assume the conversation ID is
            // the alert ID for backwards compatibility.
            state.chats[alertID].conversation = {
              status: '',
              id: conversationID,
            };
          } else {
            // Non-404 error — couldn't get conversation status.
            state.chats[alertID] = {
              conversation: { status: '', id: conversationID },
            };
          }
        } else if (conversationID.startsWith(CHAT_RAPTOR_PREFIX)) {
          const sessionID = conversationID.substring(CHAT_RAPTOR_PREFIX.length);
          const alertID = `${ALERT_PREFIX}${sessionID}`;
          const entry = state.chats[alertID];
          if (entry?.privileged_conversation) {
            entry.privileged_conversation.status = '';
          }
        }
      })
      .addCase(fetchAlertConversationStatus.fulfilled, (state, action) => {
        state.loading.alertConversationStatus = false;
        const conversationID = (action?.meta?.arg as ConversationArg)
          ?.conversationID;
        if (!conversationID) return;

        if (conversationID.includes('default-')) {
          const alertID = `alert-${conversationID.split('default-')[1]}`;
          const status = action?.payload?.data?.status;
          if (state?.chats?.[alertID]) {
            state.chats[alertID].conversation = {
              status: status ?? '',
              id: conversationID,
            };
          } else {
            state.chats[alertID] = {
              conversation: { status: status ?? '', id: conversationID },
            };
          }
        } else if (conversationID.startsWith(CHAT_RAPTOR_PREFIX)) {
          const sessionID = conversationID.substring(CHAT_RAPTOR_PREFIX.length);
          const alertID = `${ALERT_PREFIX}${sessionID}`;
          const entry = state.chats[alertID];
          if (entry?.privileged_conversation) {
            entry.privileged_conversation.status =
              action?.payload?.data?.status;
          }
        }
      })
      .addCase(deleteConversation.pending, state => {
        state.loading.alertEndChat = true;
      })
      .addCase(deleteConversation.rejected, state => {
        state.loading.alertEndChat = false;
      })
      .addCase(deleteConversation.fulfilled, (state, action) => {
        const { conversationID } = action.meta.arg;
        state.loading.alertEndChat = false;

        if (conversationID.includes('default-')) {
          const alertID = `alert-${conversationID.split('default-')[1]}`;
          if (state.chats[alertID]) {
            state.chats[alertID].conversation = {
              status: CONVERSATION_ENDED,
              id: conversationID,
            };
          }
        } else if (conversationID.startsWith(CHAT_RAPTOR_PREFIX)) {
          const sessionID = conversationID.substring(CHAT_RAPTOR_PREFIX.length);
          const alertID = `${ALERT_PREFIX}${sessionID}`;
          const entry = state.chats[alertID];
          if (entry?.privileged_conversation) {
            entry.privileged_conversation.status = CONVERSATION_ENDED;
          }
        }
      })
      .addCase('APP_INIT_START', state => state)
      .addMatcher(isLogoutAction, () => initialState);
  },
});

export const {
  setChatSelectedAlertID,
  addPrivilegedParticipants,
  addUnreadMessages,
  resetUnreadMessages,
} = chatSlice.actions;

export const chatReducer = chatSlice.reducer;

const persistConfig = { key: 'chat', storage };
const persistedReducer = persistReducer(persistConfig, chatSlice.reducer);

export default persistedReducer;
